import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  createWorkLaborEntry,
  deleteWorkLaborEntry,
  getWorkLaborEntries,
  validateWorkLaborEntry,
  type WorkLaborEntry,
} from "../../../marcos/workLaborEntriesApi";
import { getWorkers, type Worker } from "../../../trabalhadores/trabalhadoresApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";
import { formatCurrency, formatDateShort } from "../obraFormat";

interface WorkLaborTabProps {
  workId: number;
}

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Parse a localized/plain number string into a finite number, or null when blank/invalid.
const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatHours = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export function WorkLaborTab({ workId }: WorkLaborTabProps) {
  const [entries, setEntries] = useState<WorkLaborEntry[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [validatingId, setValidatingId] = useState<number | null>(null);

  const [workerId, setWorkerId] = useState("");
  const [data, setData] = useState(todayInput());
  const [horas, setHoras] = useState("");
  const [taxa, setTaxa] = useState("");
  const [notas, setNotas] = useState("");

  const loadEntries = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        setEntries(await getWorkLaborEntries(workId));
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar as horas.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const workerList = await getWorkers();
        if (cancelled) return;
        setWorkers(workerList);
      } catch {
        // Non-blocking: entries still render; the worker select just has fewer options.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeWorkers = useMemo(
    () => workers.filter((worker) => worker.isActive),
    [workers],
  );

  const workerOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      activeWorkers.map((worker) => ({
        value: String(worker.id),
        label: worker.name,
      })),
    [activeWorkers],
  );

  const resetForm = () => {
    setWorkerId("");
    setData(todayInput());
    setHoras("");
    setTaxa("");
    setNotas("");
  };

  const handleAdd = async () => {
    if (isSaving) return;
    const worker = activeWorkers.find((w) => String(w.id) === workerId);
    if (!worker) {
      setError("Selecione o trabalhador.");
      return;
    }
    const hours = parseNumber(horas);
    if (hours === null || hours <= 0) {
      setError("Indique um número de horas válido (> 0).");
      return;
    }
    if (!data) {
      setError("A data é obrigatória.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createWorkLaborEntry({
        workId,
        workerId: worker.id,
        workerName: worker.name,
        workedOn: data,
        hours,
        hourlyRate: parseNumber(taxa),
        notes: notas.trim() !== "" ? notas.trim() : null,
      });
      setEntries((prev) => [created, ...prev]);
      resetForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível adicionar as horas.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleValidate = async (entry: WorkLaborEntry) => {
    if (validatingId !== null) return;
    setValidatingId(entry.id);
    setError(null);
    try {
      const updated = await validateWorkLaborEntry(entry.id, !entry.isValidated);
      setEntries((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível atualizar o estado.",
      );
      loadEntries(true);
    } finally {
      setValidatingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteWorkLaborEntry(id);
      setEntries((prev) => prev.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setEntries((prev) => prev.filter((item) => item.id !== id));
        setConfirmDeleteId(null);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar as horas.",
        );
        loadEntries(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const totalHoras = entries.reduce(
    (sum, entry) => sum + (Number.isFinite(entry.hours) ? entry.hours : 0),
    0,
  );
  const totalCusto = entries.reduce(
    (sum, entry) => sum + (Number.isFinite(entry.cost) ? entry.cost : 0),
    0,
  );

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-card border border-border p-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SearchableSelect<string>
            label="Trabalhador"
            required
            labelClassName="text-sm text-secondary-text"
            labelStyle={{}}
            value={workerId === "" ? null : workerId}
            onChange={(next) => {
              const value = next ?? "";
              const worker = activeWorkers.find((w) => String(w.id) === value);
              const rate =
                worker &&
                worker.hourlyCost !== null &&
                worker.hourlyCost !== undefined
                  ? String(worker.hourlyCost)
                  : taxa;
              setWorkerId(value);
              setTaxa(rate);
            }}
            options={workerOptions}
            placeholder="—"
            searchPlaceholder="Procurar trabalhador…"
          />
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Data *
            <input
              type="date"
              value={data}
              onChange={(event) => setData(event.target.value)}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Horas *
            <input
              type="number"
              min={0}
              step={0.5}
              value={horas}
              onChange={(event) => setHoras(event.target.value)}
              placeholder="0"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Taxa €/h
            <input
              type="number"
              min={0}
              step={0.01}
              value={taxa}
              onChange={(event) => setTaxa(event.target.value)}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
            Notas
            <textarea
              value={notas}
              maxLength={1000}
              rows={2}
              onChange={(event) => setNotas(event.target.value)}
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={handleAdd}
            disabled={isSaving}
            className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isSaving ? "A adicionar…" : "Adicionar"}
          </button>
        </div>
      </div>

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: "820px" }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Trabalhador</th>
              <th className="px-4 py-3" style={{ width: "120px" }}>Data</th>
              <th className="px-4 py-3 text-right" style={{ width: "100px" }}>Horas</th>
              <th className="px-4 py-3 text-right" style={{ width: "110px" }}>Taxa/h</th>
              <th className="px-4 py-3 text-right" style={{ width: "130px" }}>Custo</th>
              <th className="px-4 py-3" style={{ width: "120px" }}>Estado</th>
              <th className="px-4 py-3" style={{ width: "200px" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-secondary-text">
                  Sem horas registadas. Use o formulário acima para adicionar as
                  primeiras.
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 break-words">
                    {entry.workerName || "—"}
                    {entry.notes ? (
                      <span className="block text-secondary-text text-xs">
                        {entry.notes}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDateShort(entry.workedOn)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatHours(entry.hours)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(entry.hourlyRate)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                    {formatCurrency(entry.cost)}
                  </td>
                  <td className="px-4 py-3">
                    {entry.isValidated ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 bg-green-500/10 text-green-700 border border-border"
                        style={{
                          fontSize: "var(--text-caption)",
                          borderRadius: "var(--radius)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        Validado
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-2 py-0.5 bg-muted text-secondary-text border border-border"
                        style={{
                          fontSize: "var(--text-caption)",
                          borderRadius: "var(--radius)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        Por validar
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {confirmDeleteId === entry.id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-secondary-text">Apagar?</span>
                        <button
                          type="button"
                          disabled={deletingId === entry.id}
                          onClick={() => handleDelete(entry.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {deletingId === entry.id ? "A eliminar…" : "Confirmar"}
                        </button>
                        <button
                          type="button"
                          disabled={deletingId === entry.id}
                          onClick={() => setConfirmDeleteId(null)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          disabled={validatingId === entry.id}
                          onClick={() => handleToggleValidate(entry)}
                          className="text-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validatingId === entry.id
                            ? "A guardar…"
                            : entry.isValidated
                              ? "Reabrir"
                              : "Validar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(entry.id)}
                          className="text-destructive cursor-pointer"
                        >
                          Apagar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <span className="text-secondary-text text-sm">
          Total de horas:{" "}
          <span className="text-foreground font-medium">{formatHours(totalHoras)} h</span>
          {" · "}Custo:{" "}
          <span className="text-foreground font-medium">
            {formatCurrency(totalCusto)}
          </span>
        </span>
      </div>
    </div>
  );
}
